import type {
  AssistantMessage,
  Message,
  StatusLine
} from "@/lib/order-assistant/conversation";
import type { CatalogSearched } from "@/lib/order-assistant/contract";
import { cn } from "@repo/design-system/utils";
import { CircleAlert, RotateCcw } from "lucide-react";
import { memo } from "react";

import { AssistantMarkdown } from "./AssistantMarkdown";
import {
  cardCaptions,
  CardsView,
  CATALOG_LABEL,
  CATALOG_NAMES
} from "./CardsView";
import { LinkButton } from "./LinkButton";
import { PanelView } from "./PanelView";

/** Our text for the tools we know. Tool names arrive as free strings from the
 *  service, so a name outside this map gets the generic line. */
const TOOL_STATUS = {
  search_products: "Checking the SalonStocker catalog",
  search_supplier_catalog: "Searching supplier catalogs",
  get_product_details: "Looking at product details",
  get_orders: "Checking your orders",
  get_order: "Looking up an order"
} as const;
const TOOL_STATUS_FALLBACK = "Working on it";

function isKnownTool(tool: string): tool is keyof typeof TOOL_STATUS {
  return Object.hasOwn(TOOL_STATUS, tool);
}

/** The model's own label when it sent one, else our copy for the tool. */
function statusText(line: StatusLine | null): string {
  if (line === null) return "Thinking";
  if (line.kind === "progress") return line.text;
  if (line.label) return line.label;
  return isKnownTool(line.tool) ? TOOL_STATUS[line.tool] : TOOL_STATUS_FALLBACK;
}

/**
 * What an answer searched. A catalog whose cards are on screen is already named
 * in that cards' caption, so it is left out here; when that leaves nothing the
 * line is not drawn at all, so the same words never show twice in one reply.
 */
function CatalogLine({
  catalogs,
  named
}: {
  catalogs: CatalogSearched[];
  named: ReadonlySet<CatalogSearched>;
}) {
  // `catalogs_searched` comes from the service unvalidated: list only the
  // catalogs we have a name for.
  const names = catalogs.filter(
    (catalog) =>
      catalog !== "none" &&
      Object.hasOwn(CATALOG_NAMES, catalog) &&
      !named.has(catalog)
  );
  if (names.length === 0 && named.size > 0) return null;
  return (
    <p className="text-ui-fg-muted text-xs">
      {names.length === 0
        ? CATALOG_NAMES.none
        : `${CATALOG_LABEL}: ${names
            .map((catalog) => CATALOG_NAMES[catalog])
            .join(", ")}`}
    </p>
  );
}

const INTERNAL_FAILURE = "Something went wrong. Please try again.";

/** What a failed answer says, by failure code. Only a dropped connection has
 *  its own line; every other code is the generic one. A new code fails the
 *  typecheck until it is placed here. */
const FAILURE_TEXT: Record<
  NonNullable<AssistantMessage["failure"]>["code"],
  string
> = {
  network: "The connection dropped before the answer finished.",
  limit_reached: INTERNAL_FAILURE,
  session_ended: INTERNAL_FAILURE,
  session_customer_mismatch: INTERNAL_FAILURE,
  token_expired: INTERNAL_FAILURE,
  busy: INTERNAL_FAILURE,
  disabled: INTERNAL_FAILURE,
  invalid_request: INTERNAL_FAILURE,
  internal: INTERNAL_FAILURE
};

export const AnswerView = memo(function AnswerView({
  message,
  canRetry,
  suggestionsEnabled,
  onRetry,
  onSuggestion
}: {
  message: AssistantMessage;
  canRetry: boolean;
  suggestionsEnabled: boolean;
  onRetry: () => void;
  onSuggestion: (text: string) => void;
}) {
  const streaming = message.status === "streaming";
  const captions = cardCaptions(message.cards);
  const namedByCards = new Set<CatalogSearched>(
    [...captions.values()].map((caption) => caption.catalog)
  );

  return (
    <div className="flex max-w-full flex-col gap-y-3">
      {message.text ? (
        <AssistantMarkdown
          text={message.text}
          className="text-ui-fg-base text-sm leading-6"
        />
      ) : null}

      {message.cards.map((cards) => (
        <CardsView
          key={cards.cards_id}
          event={cards}
          caption={captions.get(cards) ?? null}
        />
      ))}
      {message.panels.map((panel) => (
        <PanelView key={panel.streamId} panel={panel} />
      ))}

      {streaming ? (
        <p
          className="text-ui-fg-subtle flex items-center gap-x-2 text-sm"
          data-testid="assistant-status"
        >
          <span className="flex gap-x-1" aria-hidden="true">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className="bg-ui-fg-muted h-1.5 w-1.5 animate-pulse rounded-full"
                style={{ animationDelay: `${dot * 150}ms` }}
              />
            ))}
          </span>
          {statusText(message.statusLine)}
        </p>
      ) : null}

      {message.status === "done" && message.catalogs ? (
        <CatalogLine catalogs={message.catalogs} named={namedByCards} />
      ) : null}

      {message.status === "done" && message.truncated ? (
        <div
          role="status"
          className="border-ui-border-base bg-ui-bg-subtle flex items-start gap-x-2 rounded-lg border p-3 text-sm"
        >
          <CircleAlert
            className="text-ui-fg-subtle mt-0.5 h-4 w-4 shrink-0"
            aria-hidden="true"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-y-2">
            <p className="text-ui-fg-base">This reply was cut short.</p>
            {canRetry ? (
              <LinkButton onClick={onRetry}>
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Ask again
              </LinkButton>
            ) : null}
          </div>
        </div>
      ) : null}

      {message.status === "failed" && message.failure ? (
        <div
          role="status"
          className="border-ui-border-error bg-ui-bg-subtle flex items-start gap-x-2 rounded-lg border p-3 text-sm"
        >
          <CircleAlert
            className="text-ui-fg-error mt-0.5 h-4 w-4 shrink-0"
            aria-hidden="true"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-y-2">
            <p className="text-ui-fg-base">
              {Object.hasOwn(FAILURE_TEXT, message.failure.code)
                ? FAILURE_TEXT[message.failure.code]
                : INTERNAL_FAILURE}
            </p>
            {canRetry ? (
              <LinkButton onClick={onRetry}>
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Try again
              </LinkButton>
            ) : null}
          </div>
        </div>
      ) : null}

      {suggestionsEnabled && message.suggestions.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {message.suggestions.map((suggestion, index) => (
            // The list is replaced as a whole, never reordered, and the model
            // can repeat a suggestion: the index keeps the key unique.
            <li key={`${index}:${suggestion}`}>
              <button
                type="button"
                onClick={() => onSuggestion(suggestion)}
                className="border-ui-border-base text-ui-fg-base hover:bg-ui-bg-base-hover focus-visible:shadow-borders-focus rounded-full border px-3 py-1.5 text-left text-sm focus-visible:outline-none"
              >
                {suggestion}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
});

export const UserBubble = memo(function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <p
        className={cn(
          "max-w-sm whitespace-pre-wrap break-words rounded-lg",
          "bg-purple-600 px-3 py-2 text-sm text-white"
        )}
      >
        {text}
      </p>
    </div>
  );
});

export function MessageRow({
  message,
  isLast,
  canRetry,
  suggestionsEnabled,
  onRetry,
  onSuggestion
}: {
  message: Message;
  isLast: boolean;
  canRetry: boolean;
  suggestionsEnabled: boolean;
  onRetry: () => void;
  onSuggestion: (text: string) => void;
}) {
  return message.role === "user" ? (
    <UserBubble text={message.text} />
  ) : (
    <AnswerView
      message={message}
      canRetry={isLast && canRetry}
      suggestionsEnabled={isLast && suggestionsEnabled}
      onRetry={onRetry}
      onSuggestion={onSuggestion}
    />
  );
}
